import React, { useEffect, useMemo, useState } from 'react';
import Papa from 'papaparse';
import { FiPlusCircle, FiTrash2, FiUpload, FiDownload, FiSearch } from 'react-icons/fi';
import useStoreTimezone from '../../hooks/useStoreTimezone';
import { formatInStoreTimezone, toApiDateValue } from '../../utils/dateUtils';

const DEFAULT_TAX_RATE = 10;
const LINES_PER_PAGE = 8;
const INVOICE_PURCHASE_TYPES = [
  { value: 'Normal', label: 'Normal Purchase' },
  { value: 'Bulk', label: 'Bulk Purchase' },
];

const normalizeNumber = (value) => {
  if (value == null) return NaN;
  if (typeof value === 'number') return value;
  const cleaned = String(value)
    .replace(/[^0-9.+-]/g, '')
    .replace(/^\.+/, '');
  if (!cleaned || cleaned === '.' || cleaned === '+' || cleaned === '-' || cleaned === '+.' || cleaned === '-.') {
    return NaN;
  }
  return Number(cleaned);
};

const numberOrUndefined = (value) => {
  if (value === '' || value === null || value === undefined) {
    return undefined;
  }
  const parsed = normalizeNumber(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const parseBoolean = (value) => {
  if (value === true || value === false) return value;
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim().toLowerCase();
  if (['true', '1', 'yes', 'y'].includes(normalized)) return true;
  if (['false', '0', 'no', 'n'].includes(normalized)) return false;
  return undefined;
};

const isBulkPurchaseType = (value) => String(value || '').trim().toLowerCase() === 'bulk';

const calculateTotals = (lines = [], taxRate = DEFAULT_TAX_RATE, resolveProductForLine = null) => {
  const subtotal = lines.reduce((sum, line) => {
    const qty = Number(line.quantity) || 0;
    const unitCost = Number(line.unitCost) || 0;
    return sum + qty * unitCost;
  }, 0);
  const taxableSubtotal = lines.reduce((sum, line) => {
    const inferredGstApplicable =
      line?.gstApplicable !== undefined && line?.gstApplicable !== null
        ? line.gstApplicable !== false
        : resolveProductForLine?.(line)?.gstApplicable !== false;
    if (!inferredGstApplicable) return sum;
    const qty = Number(line.quantity) || 0;
    const unitCost = Number(line.unitCost) || 0;
    return sum + (qty * unitCost);
  }, 0);
  const rate = Number.isFinite(Number(taxRate)) ? Number(taxRate) : DEFAULT_TAX_RATE;
  const taxAmount = taxableSubtotal * (rate / 100);
  return {
    subtotal,
    taxAmount,
    total: subtotal + taxAmount,
    rate,
  };
};

const blankLine = () => ({
  name: '',
  description: '',
  sku: '',
  barcode: '',
  gstApplicable: true,
  quantity: 1,
  unitCost: '',
  price: '',
  notes: '',
});

const InvoiceForm = ({
  invoice,
  suppliers,
  purchaseOrders,
  products = [],
  defaultStoreId,
  defaultStoreName,
  onSave,
  onCancel,
  onAddNote,
  onLoadPurchaseOrder,
  readOnly = false,
}) => {
  const timezone = useStoreTimezone();
  const isReadOnly = !!readOnly;
  const [formError, setFormError] = useState('');
  const [noteDraft, setNoteDraft] = useState('');
  const [notes, setNotes] = useState([]);
  const [linePage, setLinePage] = useState(1);
  const [lineSearch, setLineSearch] = useState('');
  const [form, setForm] = useState({
    supplier: '',
    store: defaultStoreId || '',
    purchaseOrder: '',
    purchaseType: 'Normal',
    invoiceNumber: '',
    invoiceDate: '',
    dueDate: '',
    scheduledPaymentDate: '',
    paidDate: '',
    status: 'Unpaid',
    invoiceTaxRate: DEFAULT_TAX_RATE,
    supplierInvoiceAmount: '',
    lines: [],
  });

  const productSkuMap = useMemo(() => {
    const map = new Map();
    (products || []).forEach((product) => {
      if (typeof product?.sku === 'string' && product.sku.trim()) {
        map.set(product.sku.trim().toLowerCase(), product);
      }
    });
    return map;
  }, [products]);

  const productBarcodeMap = useMemo(() => {
    const map = new Map();
    (products || []).forEach((product) => {
      if (typeof product?.barcode === 'string' && product.barcode.trim()) {
        map.set(product.barcode.trim(), product);
      }
    });
    return map;
  }, [products]);

  const resolveProductForLine = useMemo(
    () => (line) => {
      if (!line) return null;
      const skuKey = typeof line.sku === 'string' ? line.sku.trim().toLowerCase() : '';
      if (skuKey && productSkuMap.has(skuKey)) {
        return productSkuMap.get(skuKey);
      }
      const barcodeKey = typeof line.barcode === 'string' ? line.barcode.trim() : '';
      if (barcodeKey && productBarcodeMap.has(barcodeKey)) {
        return productBarcodeMap.get(barcodeKey);
      }
      return null;
    },
    [productSkuMap, productBarcodeMap]
  );

  useEffect(() => {
    if (invoice) {
      setForm({
        supplier: invoice.supplier?._id || invoice.supplier || '',
        store: invoice.store?._id || invoice.store || defaultStoreId,
        purchaseOrder: invoice.purchaseOrder?._id || invoice.purchaseOrder || '',
        purchaseType: invoice.purchaseType || 'Normal',
        invoiceNumber: invoice.invoiceNumber || '',
        invoiceDate: toApiDateValue(invoice.invoiceDate),
        dueDate: toApiDateValue(invoice.dueDate),
        scheduledPaymentDate: toApiDateValue(invoice.scheduledPaymentDate),
        paidDate: toApiDateValue(invoice.paidDate),
        status: invoice.status || 'Unpaid',
        invoiceTaxRate: invoice.invoiceTaxRate ?? DEFAULT_TAX_RATE,
        supplierInvoiceAmount: invoice.supplierInvoiceAmount ?? '',
        lines: (invoice.lines || []).map((line) => ({
          name: line.name || line.description || '',
          description: line.description || '',
          sku: line.sku || '',
          barcode: line.barcode || '',
          gstApplicable: line.gstApplicable !== undefined ? line.gstApplicable : true,
          quantity: line.quantity ?? 0,
          unitCost: line.unitCost ?? '',
          price: line.price ?? '',
          notes: line.notes || '',
        })),
      });
      setNotes(Array.isArray(invoice.notes) ? invoice.notes : []);
    } else {
      setForm((prev) => ({
        ...prev,
        store: defaultStoreId || '',
        status: 'Unpaid',
        purchaseType: 'Normal',
        invoiceTaxRate: DEFAULT_TAX_RATE,
        supplierInvoiceAmount: '',
        purchaseOrder: '',
        lines: [],
      }));
      setNotes([]);
    }
    setNoteDraft('');
    setFormError('');
    setLinePage(1);
    setLineSearch('');
  }, [invoice, defaultStoreId]);

  const totals = useMemo(
    () => calculateTotals(form.lines, form.invoiceTaxRate, resolveProductForLine),
    [form.lines, form.invoiceTaxRate, resolveProductForLine]
  );

  const filteredLines = useMemo(() => {
    const term = lineSearch.trim().toLowerCase();
    return form.lines
      .map((line, index) => ({ line, index }))
      .filter(({ line }) => {
        if (!term) return true;
        const haystack = [
          line.name,
          line.description,
          line.sku,
          line.barcode,
        ]
          .filter(Boolean)
          .map((value) => String(value).toLowerCase());
        return haystack.some((value) => value.includes(term));
      });
  }, [form.lines, lineSearch]);

  const totalLinePages = Math.max(1, Math.ceil(filteredLines.length / LINES_PER_PAGE));
  const currentLinePage = Math.min(linePage, totalLinePages);
  const lineStart = (currentLinePage - 1) * LINES_PER_PAGE;
  const lineEnd = lineStart + LINES_PER_PAGE;
  const pagedLines = filteredLines.slice(lineStart, lineEnd);

  useEffect(() => {
    if (linePage > totalLinePages) {
      setLinePage(totalLinePages);
    }
  }, [linePage, totalLinePages]);

  useEffect(() => {
    if (!isBulkPurchaseType(form.purchaseType)) return;
    setForm((prev) => {
      const hasNonZero = prev.lines.some((line) => Number(line.unitCost) !== 0);
      if (!hasNonZero) {
        return prev;
      }
      return {
        ...prev,
        lines: prev.lines.map((line) => ({ ...line, unitCost: 0 })),
      };
    });
  }, [form.purchaseType]);

  const handleChange = (event) => {
    if (isReadOnly) return;
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const updateLine = (index, field, value) => {
    if (isReadOnly) return;
    setForm((prev) => {
      const updated = [...prev.lines];
      const nextValue = field === 'unitCost' && isBulkPurchaseType(prev.purchaseType) ? 0 : value;
      updated[index] = { ...updated[index], [field]: nextValue };
      return { ...prev, lines: updated };
    });
  };

  const addLine = () => {
    if (isReadOnly) return;
    setForm((prev) => {
      const nextLines = [...prev.lines, blankLine()];
      setLinePage(Math.ceil(nextLines.length / LINES_PER_PAGE));
      return { ...prev, lines: nextLines };
    });
  };

  const removeLine = (index) => {
    if (isReadOnly) return;
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.filter((_, idx) => idx !== index),
    }));
  };

  const handlePurchaseOrderChange = async (event) => {
    if (isReadOnly) return;
    const nextId = event.target.value;
    setForm((prev) => ({ ...prev, purchaseOrder: nextId }));
    if (!nextId || !onLoadPurchaseOrder) return;
    try {
      const details = await onLoadPurchaseOrder(nextId);
      if (!details) return;
      const nextSupplier = details.supplier?._id || details.supplier || '';
      const bulkPurchase = isBulkPurchaseType(form.purchaseType);
      const nextLines = (details.lines || []).map((line) => ({
        name: line.product?.name || line.description || '',
        description: line.description || '',
        sku: line.sku || line.product?.sku || '',
        barcode: line.barcode || line.product?.barcode || '',
        gstApplicable: line.gstApplicable !== undefined ? line.gstApplicable : true,
        quantity: line.quantity ?? 0,
        unitCost: bulkPurchase ? 0 : line.unitCost ?? '',
        price: line.price ?? '',
        notes: line.notes || '',
      }));
      const shouldReplace = !form.lines.length || window.confirm('Replace current line items with purchase order lines?');
      setForm((prev) => ({
        ...prev,
        supplier: nextSupplier || prev.supplier,
        invoiceNumber: details.invoiceNumber || prev.invoiceNumber,
        invoiceDate: toApiDateValue(details.invoiceDate) || prev.invoiceDate,
        dueDate: toApiDateValue(details.expectedDate) || prev.dueDate,
        lines: shouldReplace ? nextLines : prev.lines,
      }));
    } catch (err) {
      setFormError('Unable to load purchase order details.');
    }
  };

  const handleImportCsv = (event) => {
    if (isReadOnly) return;
    const file = event.target.files?.[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const bulkPurchase = isBulkPurchaseType(form.purchaseType);
        const rows = Array.isArray(results.data) ? results.data : [];
        const newLines = [];
        const pickValue = (lookup, keys) => {
          const entry = keys.find((key) => lookup[key] !== undefined && lookup[key] !== '');
          return entry ? lookup[entry] : '';
        };
        const pickNumber = (lookup, keys, fallback) => {
          const raw = pickValue(lookup, keys);
          const parsed = normalizeNumber(raw);
          return Number.isFinite(parsed) ? parsed : fallback;
        };

        rows.forEach((row, index) => {
          const lookup = {};
          Object.keys(row || {}).forEach((key) => {
            lookup[key.trim().toLowerCase()] = row[key];
          });
          const name =
            pickValue(lookup, ['product name', 'product', 'item', 'name', 'description', 'item name']) ||
            `Imported item ${index + 1}`;
          const sku = pickValue(lookup, ['sku', 'item code', 'product code', 'code']);
          const rawBarcode = pickValue(lookup, ['barcode', 'ean', 'ean13', 'upc']) || '';
          const barcode = typeof rawBarcode === 'string' ? rawBarcode.trim() : '';
          const gstApplicable = parseBoolean(
            pickValue(lookup, ['gst applicable', 'gst', 'gst applicable (true/false)'])
          );
          const quantity = pickNumber(
            lookup,
            ['quantity', 'qty', 'stock', 'stock quantity', 'qty ordered', 'order quantity'],
            0
          );
          const unitCost = pickNumber(
            lookup,
            ['unit cost', 'unitcost', 'cost price', 'costprice', 'cost', 'buy price'],
            undefined
          ) ?? 0;
          const price = pickNumber(lookup, ['retail price', 'price', 'sell price', 'selling price'], undefined);
          const description = pickValue(lookup, ['description', 'details']) || name;

          newLines.push({
            name,
            description,
            sku,
            barcode,
            gstApplicable,
            quantity,
            unitCost: bulkPurchase ? 0 : unitCost,
            price,
            notes: '',
          });
        });

        if (newLines.length) {
          setForm((prev) => ({ ...prev, lines: [...prev.lines, ...newLines] }));
        }
        event.target.value = '';
      },
      error: () => {
        setFormError('Unable to parse CSV. Expected headers include Product Name, SKU, Barcode, Retail Price, Unit Cost, Stock, Quantity, Description, GST Applicable.');
        event.target.value = '';
      },
    });
  };

  const handleDownloadTemplate = () => {
    const headers = [
      'Product Name',
      'SKU',
      'Barcode',
      'Retail Price',
      'Unit Cost',
      'Stock',
      'Quantity',
      'Description',
      'GST Applicable (true/false)',
    ];
    const csvContent = `data:text/csv;charset=utf-8,${headers.join(',')}`;
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'invoice_template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (isReadOnly) {
      onCancel();
      return;
    }
    setFormError('');
    if (!form.supplier) {
      setFormError('Supplier is required.');
      return;
    }
    if (!form.dueDate) {
      setFormError('Due date is required.');
      return;
    }
    if (form.purchaseOrder && form.lines.length === 0) {
      setFormError('Line items are required when linking a purchase order.');
      return;
    }
    const payloadLines = form.lines
      .filter((line) => {
        const hasValue =
          line.name ||
          line.sku ||
          line.barcode ||
          numberOrUndefined(line.quantity) ||
          numberOrUndefined(line.unitCost);
        return Boolean(hasValue);
      })
      .map((line) => ({
        name: line.name || undefined,
        description: line.description || line.name || undefined,
        sku: line.sku || undefined,
        barcode: line.barcode || undefined,
        gstApplicable:
          line.gstApplicable === undefined || line.gstApplicable === null
            ? undefined
            : Boolean(line.gstApplicable),
        quantity: numberOrUndefined(line.quantity) ?? 0,
        unitCost: isBulkPurchaseType(form.purchaseType) ? 0 : numberOrUndefined(line.unitCost) ?? 0,
        price: numberOrUndefined(line.price),
        notes: line.notes || undefined,
      }));

    const payload = {
      supplier: form.supplier,
      store: form.store || defaultStoreId,
      purchaseOrder: form.purchaseOrder || undefined,
      invoiceNumber: form.invoiceNumber || undefined,
      invoiceDate: form.invoiceDate || undefined,
      dueDate: form.dueDate || undefined,
      scheduledPaymentDate: form.scheduledPaymentDate || undefined,
      paidDate: form.paidDate || undefined,
      status: form.status || 'Unpaid',
      purchaseType: form.purchaseType || 'Normal',
      invoiceTaxRate: totals.rate,
      supplierInvoiceAmount: numberOrUndefined(form.supplierInvoiceAmount),
      lines: payloadLines,
    };
    if (!invoice && noteDraft.trim()) {
      payload.notes = [{ note: noteDraft.trim() }];
    }
    onSave(payload);
  };

  const handleAddNote = async () => {
    if (!noteDraft.trim() || !onAddNote || !invoice?._id) return;
    try {
      const updated = await onAddNote(noteDraft.trim());
      if (updated?.notes) {
        setNotes(updated.notes);
      }
      setNoteDraft('');
    } catch (err) {
      setFormError('Unable to add note.');
    }
  };

  const isPoLocked = !!invoice?.purchaseOrder;
  const showSupplierReadonly = isPoLocked || isReadOnly;
  const selectedSupplierName = suppliers.find((supplier) => supplier._id === form.supplier)?.name || '';
  const statusBadgeClass = {
    Unpaid: 'bg-amber-100 text-amber-800',
    Scheduled: 'bg-blue-100 text-blue-800',
    Paid: 'bg-emerald-100 text-emerald-800',
  }[form.status] || 'bg-gray-100 text-gray-700';
  const supplierInvoiceAmountNumber = numberOrUndefined(form.supplierInvoiceAmount);
  const supplierVariance =
    supplierInvoiceAmountNumber === undefined ? null : Number(supplierInvoiceAmountNumber) - Number(totals.total);
  const supplierVarianceClass =
    supplierVariance == null
      ? 'text-gray-500'
      : supplierVariance === 0
        ? 'text-emerald-700'
        : supplierVariance > 0
          ? 'text-amber-700'
          : 'text-red-700';
  const labelClass = 'block text-[11px] font-semibold uppercase tracking-wider text-gray-500';
  const inputClass =
    'mt-1 w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-primary focus:ring-primary disabled:bg-gray-100 disabled:text-gray-500';
  const readOnlyClass = 'mt-1 w-full rounded-md border-gray-200 bg-gray-100 text-sm text-gray-700';

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {formError && <p className="rounded-md border border-red-200 bg-red-50 p-3 text-red-700">{formError}</p>}

      <section className="rounded-xl border border-gray-200 bg-gradient-to-b from-white to-gray-50 shadow-sm">
        <div className="flex flex-col gap-4 border-b border-dashed border-gray-300 px-5 py-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-500">Supplier Invoice</p>
            <h3 className="mt-1 text-2xl font-semibold text-gray-900">{form.invoiceNumber || 'Draft Invoice'}</h3>
            <p className="mt-1 text-sm text-gray-600">
              {selectedSupplierName || 'Select supplier'} {form.purchaseOrder ? `· PO ${form.purchaseOrder}` : ''}
            </p>
          </div>
          <div className="space-y-2 text-left md:text-right">
            <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${statusBadgeClass}`}>
              {form.status}
            </span>
            <p className="font-mono text-xs text-gray-600">Store: {defaultStoreName || form.store || 'Not selected'}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-2 px-5 py-3 font-mono text-xs text-gray-600 sm:grid-cols-3">
          <p className="flex justify-between border-b border-dashed border-gray-200 pb-1 sm:border-b-0 sm:pb-0">
            <span>Invoice Date</span>
            <span>{form.invoiceDate || '-'}</span>
          </p>
          <p className="flex justify-between border-b border-dashed border-gray-200 pb-1 sm:border-b-0 sm:pb-0">
            <span>Due Date</span>
            <span>{form.dueDate || '-'}</span>
          </p>
          <p className="flex justify-between">
            <span>Scheduled</span>
            <span>{form.scheduledPaymentDate || '-'}</span>
          </p>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="space-y-6 xl:col-span-2">
          <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-dashed border-gray-300 px-5 py-3">
              <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-700">Invoice Details</h4>
            </div>
            <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-2">
              <div>
                <label className={labelClass}>Supplier</label>
                <select
                  name="supplier"
                  value={form.supplier}
                  onChange={handleChange}
                  disabled={showSupplierReadonly}
                  className={inputClass}
                >
                  <option value="">Select supplier</option>
                  {suppliers.map((supplier) => (
                    <option key={supplier._id} value={supplier._id}>
                      {supplier.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Store</label>
                <input value={defaultStoreName || form.store} disabled className={readOnlyClass} />
              </div>
              <div>
                <label className={labelClass}>Purchase Order</label>
                <select
                  name="purchaseOrder"
                  value={form.purchaseOrder}
                  onChange={handlePurchaseOrderChange}
                  disabled={isReadOnly || isPoLocked}
                  className={inputClass}
                >
                  <option value="">None</option>
                  {purchaseOrders.map((order) => (
                    <option key={order._id} value={order._id}>
                      {order.poNumber} {order.supplier?.name ? `- ${order.supplier.name}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Invoice Number</label>
                <input
                  name="invoiceNumber"
                  value={form.invoiceNumber}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  className={inputClass}
                  placeholder="Supplier reference"
                />
              </div>
              <div>
                <label className={labelClass}>Invoice Date</label>
                <input
                  type="date"
                  name="invoiceDate"
                  value={form.invoiceDate}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Due Date</label>
                <input
                  type="date"
                  name="dueDate"
                  value={form.dueDate}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  required
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Status</label>
                <select
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  className={inputClass}
                >
                  <option value="Unpaid">Unpaid</option>
                  <option value="Scheduled">Scheduled</option>
                  <option value="Paid">Paid</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Purchase Type</label>
                <select
                  name="purchaseType"
                  value={form.purchaseType}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  className={inputClass}
                >
                  {INVOICE_PURCHASE_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              {form.status === 'Scheduled' && (
                <div>
                  <label className={labelClass}>Scheduled Payment Date</label>
                  <input
                    type="date"
                    name="scheduledPaymentDate"
                    value={form.scheduledPaymentDate}
                    onChange={handleChange}
                    disabled={isReadOnly}
                    className={inputClass}
                  />
                </div>
              )}
              {form.status === 'Paid' && (
                <div>
                  <label className={labelClass}>Paid Date</label>
                  <input
                    type="date"
                    name="paidDate"
                    value={form.paidDate}
                    onChange={handleChange}
                    disabled={isReadOnly}
                    className={inputClass}
                  />
                </div>
              )}
            </div>
          </section>

          <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex flex-col gap-3 border-b border-gray-200 px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h4 className="text-sm font-semibold uppercase tracking-[0.12em] text-gray-700">Line Items</h4>
                <p className="text-xs text-gray-500">
                  Import supplier CSVs or enter lines manually.
                  {isBulkPurchaseType(form.purchaseType) ? ' Bulk purchase mode sets all line costs to $0.00.' : ''}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative w-full sm:w-72">
                  <FiSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    value={lineSearch}
                    onChange={(e) => {
                      setLineSearch(e.target.value);
                      setLinePage(1);
                    }}
                    className="h-9 w-full rounded-md border border-gray-300 bg-white pl-9 pr-3 text-sm text-gray-700 shadow-sm focus:border-primary focus:ring-primary"
                    placeholder="Search product, SKU, barcode"
                  />
                </div>
                {!isReadOnly && (
                  <>
                    <label className="inline-flex h-9 cursor-pointer items-center rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50">
                      <FiUpload className="mr-2" /> Import CSV
                      <input type="file" accept=".csv" onChange={handleImportCsv} className="hidden" />
                    </label>
                    <button
                      type="button"
                      onClick={handleDownloadTemplate}
                      className="inline-flex h-9 items-center rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      <FiDownload className="mr-2" /> CSV Template
                    </button>
                    <button
                      type="button"
                      onClick={addLine}
                      className="inline-flex h-9 items-center rounded-md bg-primary px-3 text-sm font-semibold text-white hover:bg-primary-dark"
                    >
                      <FiPlusCircle className="mr-2" /> Add Line
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-[980px] w-full border-collapse text-sm">
                <thead className="sticky top-0 z-10 bg-gray-100 text-[11px] uppercase tracking-[0.08em] text-gray-600">
                  <tr>
                    <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">Product</th>
                    <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">SKU</th>
                    <th className="border-b border-gray-200 px-3 py-2 text-left font-semibold">Barcode</th>
                    <th className="border-b border-gray-200 px-3 py-2 text-center font-semibold">GST</th>
                    <th className="border-b border-gray-200 px-3 py-2 text-right font-semibold">Qty</th>
                    <th className="border-b border-gray-200 px-3 py-2 text-right font-semibold">Unit Cost</th>
                    <th className="border-b border-gray-200 px-3 py-2 text-right font-semibold">Line Total</th>
                    <th className="border-b border-gray-200 px-3 py-2 text-right font-semibold">Retail Price</th>
                    <th className="hidden border-b border-gray-200 px-3 py-2 text-left font-semibold 2xl:table-cell">Description</th>
                    {!isReadOnly && <th className="border-b border-gray-200 px-3 py-2 text-right font-semibold">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {pagedLines.length === 0 && (
                    <tr>
                      <td colSpan={isReadOnly ? 9 : 10} className="px-6 py-8 text-center text-gray-500">
                        {form.lines.length === 0 ? 'No line items added yet.' : 'No line items match your search.'}
                      </td>
                    </tr>
                  )}
                  {pagedLines.map(({ line, index }, rowIndex) => {
                    const unitCostValue = isBulkPurchaseType(form.purchaseType) ? 0 : Number(line.unitCost) || 0;
                    const lineTotal = (Number(line.quantity) || 0) * unitCostValue;
                    return (
                      <tr key={`line-${index}`} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="border-b border-gray-200 px-3 py-2">
                          <input
                            value={line.name}
                            onChange={(e) => updateLine(index, 'name', e.target.value)}
                            disabled={isReadOnly}
                            className="h-8 w-44 rounded-md border border-gray-200 bg-white px-2 text-sm disabled:border-transparent disabled:bg-transparent disabled:px-0"
                          />
                        </td>
                        <td className="border-b border-gray-200 px-3 py-2">
                          <input
                            value={line.sku}
                            onChange={(e) => updateLine(index, 'sku', e.target.value)}
                            disabled={isReadOnly}
                            className="h-8 w-28 rounded-md border border-gray-200 bg-white px-2 text-sm disabled:border-transparent disabled:bg-transparent disabled:px-0"
                          />
                        </td>
                        <td className="border-b border-gray-200 px-3 py-2">
                          <input
                            value={line.barcode}
                            onChange={(e) => updateLine(index, 'barcode', e.target.value)}
                            disabled={isReadOnly}
                            className="h-8 w-36 rounded-md border border-gray-200 bg-white px-2 text-sm disabled:border-transparent disabled:bg-transparent disabled:px-0"
                          />
                        </td>
                        <td className="border-b border-gray-200 px-3 py-2 text-center">
                          <select
                            value={line.gstApplicable === false ? 'false' : 'true'}
                            onChange={(e) => updateLine(index, 'gstApplicable', e.target.value === 'true')}
                            disabled={isReadOnly}
                            className="h-8 rounded-md border border-gray-200 bg-white px-2 text-sm disabled:border-transparent disabled:bg-transparent disabled:px-0"
                          >
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                          </select>
                        </td>
                        <td className="border-b border-gray-200 px-3 py-2 text-right">
                          <input
                            type="number"
                            value={line.quantity}
                            onChange={(e) => updateLine(index, 'quantity', e.target.value)}
                            disabled={isReadOnly}
                            className="h-8 w-20 rounded-md border border-gray-200 bg-white px-2 text-right text-sm disabled:border-transparent disabled:bg-transparent disabled:px-0"
                          />
                        </td>
                        <td className="border-b border-gray-200 px-3 py-2 text-right">
                          <input
                            type="number"
                            value={isBulkPurchaseType(form.purchaseType) ? 0 : line.unitCost}
                            onChange={(e) => updateLine(index, 'unitCost', e.target.value)}
                            disabled={isReadOnly || isBulkPurchaseType(form.purchaseType)}
                            className="h-8 w-24 rounded-md border border-gray-200 bg-white px-2 text-right text-sm disabled:border-transparent disabled:bg-transparent disabled:px-0"
                          />
                        </td>
                        <td className="border-b border-gray-200 px-3 py-2 text-right font-medium text-gray-700">${lineTotal.toFixed(2)}</td>
                        <td className="border-b border-gray-200 px-3 py-2 text-right">
                          <input
                            type="number"
                            value={line.price}
                            onChange={(e) => updateLine(index, 'price', e.target.value)}
                            disabled={isReadOnly}
                            className="h-8 w-24 rounded-md border border-gray-200 bg-white px-2 text-right text-sm disabled:border-transparent disabled:bg-transparent disabled:px-0"
                          />
                        </td>
                        <td className="hidden border-b border-gray-200 px-3 py-2 2xl:table-cell">
                          <input
                            value={line.description}
                            onChange={(e) => updateLine(index, 'description', e.target.value)}
                            disabled={isReadOnly}
                            className="h-8 w-60 rounded-md border border-gray-200 bg-white px-2 text-sm disabled:border-transparent disabled:bg-transparent disabled:px-0"
                          />
                        </td>
                        {!isReadOnly && (
                          <td className="border-b border-gray-200 px-3 py-2 text-right">
                            <button
                              type="button"
                              onClick={() => removeLine(index)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-red-100 text-red-600 hover:bg-red-50 hover:text-red-800"
                            >
                              <FiTrash2 />
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
              <span>
                Showing {filteredLines.length === 0 ? 0 : lineStart + 1}-{Math.min(lineEnd, filteredLines.length)} of{' '}
                {filteredLines.length} line items
              </span>
              <div className="space-x-2">
                <button
                  type="button"
                  onClick={() => setLinePage((prev) => Math.max(1, prev - 1))}
                  disabled={currentLinePage <= 1}
                  className="rounded border px-3 py-1 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => setLinePage((prev) => Math.min(totalLinePages, prev + 1))}
                  disabled={currentLinePage >= totalLinePages}
                  className="rounded border px-3 py-1 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </section>

          <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-dashed border-gray-300 px-5 py-3">
              <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-700">Invoice Notes</h4>
            </div>
            <div className="p-5">
              {invoice && (
                <>
                  {notes.length ? (
                    <div className="space-y-2">
                      {notes.map((note, index) => (
                        <div key={`note-${index}`} className="rounded-md border border-gray-200 bg-gray-50 p-3">
                          <p className="text-sm text-gray-800">{note.note}</p>
                          {note.createdAt && (
                            <p className="mt-1 text-xs text-gray-500">
                              {note.createdBy?.name || note.createdBy?.email || note.createdBy?.userId
                                ? `${note.createdBy?.name || note.createdBy?.email || note.createdBy?.userId} · `
                                : ''}
                              {formatInStoreTimezone(note.createdAt, 'dd/MM/yyyy hh:mm a', timezone)}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No notes added yet.</p>
                  )}
                </>
              )}
              {!isReadOnly && (
                <div className="mt-3">
                  <textarea
                    rows={2}
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    className="w-full rounded-md border-gray-300 shadow-sm"
                    placeholder={invoice ? 'Add a new note' : 'Add notes for this invoice'}
                  />
                  {invoice ? (
                    <button
                      type="button"
                      onClick={handleAddNote}
                      disabled={!noteDraft.trim()}
                      className="mt-2 rounded-md bg-gray-900 px-3 py-2 text-sm text-white disabled:opacity-50"
                    >
                      Add Note
                    </button>
                  ) : (
                    <p className="mt-2 text-xs text-gray-500">Notes will be saved when the invoice is created.</p>
                  )}
                </div>
              )}
            </div>
          </section>
        </div>

        <aside className="space-y-6">
          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-700">Invoice Summary</h4>
            <div className="mt-4 space-y-3 font-mono text-sm text-gray-700">
              <div className="flex items-center justify-between">
                <span>Line Count</span>
                <span>{form.lines.length}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Subtotal</span>
                <span>${totals.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Tax</span>
                <span>${totals.taxAmount.toFixed(2)}</span>
              </div>
              <div className="border-t border-dashed border-gray-300 pt-3 text-base font-semibold">
                <div className="flex items-center justify-between">
                  <span>Total</span>
                  <span>${totals.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
            <div className="mt-5 space-y-4">
              <div>
                <label className={labelClass}>Invoice Tax (%)</label>
                <input
                  type="number"
                  name="invoiceTaxRate"
                  min="0"
                  step="0.01"
                  value={form.invoiceTaxRate}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Supplier Invoice Amount</label>
                <input
                  type="number"
                  name="supplierInvoiceAmount"
                  min="0"
                  step="0.01"
                  value={form.supplierInvoiceAmount}
                  onChange={handleChange}
                  disabled={isReadOnly}
                  className={inputClass}
                  placeholder="Supplier total"
                />
                <p className={`mt-1 text-xs ${supplierVarianceClass}`}>
                  {supplierVariance == null
                    ? 'Enter supplier invoice amount to compare.'
                    : `Difference vs calculated total: ${supplierVariance >= 0 ? '+' : '-'}$${Math.abs(supplierVariance).toFixed(2)}`}
                </p>
              </div>
            </div>
          </section>
        </aside>
      </div>

      <div className="flex justify-end gap-3 border-t border-dashed border-gray-300 pt-5">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border px-4 py-2 text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        {!isReadOnly && (
          <button type="submit" className="rounded-md bg-primary px-4 py-2 text-white hover:bg-primary-dark">
            {invoice ? 'Update Invoice' : 'Create Invoice'}
          </button>
        )}
      </div>
    </form>
  );
};

export default InvoiceForm;
